import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Message } from 'semantic-ui-react';
import { useCreateZone, useGetDevices } from 'services/2n/zones';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { Grid } from 'components/layout/Grid';
import { Brikk } from 'components/Brikk';
import { ShouldRender } from 'components/ShouldRender';
import { MultiSelect } from 'components/form-elements/selects/MultiSelect';
import addResourceSchema from 'utils/validation/schemas/addResourceSchema';
import styles from '../../../styles.module.scss';
import { AccessManagement2NModalContext } from '../../../AccessManagement2NModalContext';

export const AddResourceForm = () => {
  const intl = useIntl();
  const { setSelectedResource, setIsAddingResource } = useContext(AccessManagement2NModalContext);

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(addResourceSchema),
    defaultValues: {
      resourceName: '',
      resourceDescription: '',
      devicesInThisResource: '',
    },
  });

  const { mutateAsync: createZone, isLoading: isCreating } = useCreateZone();

  const { devices } = useGetDevices();
  const devicesOptions = devices.map((device) => ({ value: device.Name, label: device.Name, id: device.Id }));

  const onSubmitAsync = async (data) => {
    const formattedDevicesInThisResource = data.devicesInThisResource.map((device) => device.id);
    const payload = {
      name: data.resourceName,
      deviceIds: formattedDevicesInThisResource,
    };
    await createZone(payload);
    setSelectedResource(null);
    setIsAddingResource(false);
  };

  return (
    <div className={styles.container}>
      <Brikk.Heading
        heading="addNewResource"
      />
 
      <Form
        id="add-resource-form"
        className={styles.formContainer}
        onSubmit={handleSubmit(onSubmitAsync)}
      >
        <div className={styles.formWrapper}>
          <Grid
            noPadding
            oneColumn
          >
            <div className={styles.form}>

              <Controller
                name="resourceName"
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, onChange, value } }) => (
                  <LabeledInput
                    labelIntl="resourceName"
                    id="resource-name"
                    value={value}
                    inputRef={ref}
                    onChange={onChange}
                    hasError={!!errors.name}
                  />
                )}
              />

              <ShouldRender condition={!!errors.resourceName}>
                <Message negative>
                  <Message.Content>
                    {errors.resourceName && (
                      <FormattedMessage id={errors.resourceName.message} />
                    )}
                  </Message.Content>
                </Message>
              </ShouldRender>

              <Controller
                name="devicesInThisResource"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    options={devicesOptions}
                    title={intl.formatMessage({ id: 'devicesInThisResource' })}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

            </div>
          </Grid>
        </div>

        <div className={styles.formButtons}>
          <Button
            basic
            name="cancel-add-resource"
            type="button"
            onClick={() => {
              setSelectedResource(null);
              setIsAddingResource(false);
            }}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            primary
            type="submit"
            name="submit-add-resource"
            disabled={!isDirty || isCreating}
            loading={isCreating}
          >
            <FormattedMessage id="addResource" />
          </Button>
        </div>
      </Form>
 
    </div>
  );
};
